/**
 * Cuadre de Caja — pantalla unificada de los tres cuadres.
 *
 * La vista principal es el reporte completo del día (ares7070: recibos,
 * resumen de caja y gastos) embebido. Arriba, dos tarjetas de acción abren
 * en diálogo a pantalla completa el conteo de efectivo (Cuadre Desglosado,
 * programa cuadrecaja) y el resumen cobrado por sucursal (ares7075).
 *
 * Sustituye las entradas de menú de cuadrecajacompleto.js, cuadrecaja.js y
 * cuadrecajaresumen.js; los tres componentes se reutilizan.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, ActionCard } from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import CuadreCajaCompleto from "modules/facturacion/cuadrecajacompleto";
import CuadreCaja from "modules/facturacion/cuadrecaja";
import CuadreCajaResumen from "modules/pagos/cuadrecajaresumen";
import useAccionInicial from "lib/useAccionInicial";

export default function Cuadres() {
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  // Permisos por programa ARES (el backend re-valida en permisos.py).
  const puedeCompleto = tienePermisosModulo(roleDetalles, "ares7070");
  const puedeConteo = tienePermisosModulo(roleDetalles, "cuadrecaja");
  const puedeResumen = tienePermisosModulo(roleDetalles, "ares7075");

  const [conteoOpen, setConteoOpen] = useState(false);
  const [resumenOpen, setResumenOpen] = useState(false);

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    conteo: { permitido: puedeConteo, abrir: () => setConteoOpen(true) },
    resumen: { permitido: puedeResumen, abrir: () => setResumenOpen(true) },
  });

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Cuadre de Caja"
        subtitle="Reporte del día, conteo de efectivo y resumen por sucursal en un solo lugar"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Accesos a las otras dos vistas del cuadre */}
        {(puedeConteo || puedeResumen) && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {puedeConteo && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={PointOfSaleOutlinedIcon}
                  titulo="Conteo de efectivo"
                  descripcion="Cuadre desglosado: billetes, métodos de pago y monedas"
                  color={FP.color.success}
                  onClick={() => setConteoOpen(true)}
                />
              </Grid>
            )}
            {puedeResumen && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={SummarizeOutlinedIcon}
                  titulo="Resumen por sucursal"
                  descripcion="Cobrado por sucursal: capital, interés, mora, iniciales y contado"
                  color={FP.color.accentBlue}
                  onClick={() => setResumenOpen(true)}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Vista principal: el reporte completo del día, embebido */}
        {puedeCompleto && <CuadreCajaCompleto embedded />}
      </Box>

      {/* Conteo de efectivo (Cuadre Desglosado) a pantalla completa */}
      <Dialog fullScreen open={conteoOpen} onClose={() => setConteoOpen(false)}>
        <CuadreCaja embedded onExit={() => setConteoOpen(false)} />
      </Dialog>

      {/* Resumen por sucursal a pantalla completa */}
      <Dialog fullScreen open={resumenOpen} onClose={() => setResumenOpen(false)}>
        <CuadreCajaResumen embedded onExit={() => setResumenOpen(false)} />
      </Dialog>
    </Box>
  );
}
